// packages/common/react-async/src/useAsyncEffect.ts
import { useEffect } from "react";
var useAsyncEffect = (callback, destructor, deps) => {
  const [effectDestructor, effectDeps] = typeof destructor === "function" ? [
    destructor,
    deps
  ] : [
    void 0,
    destructor
  ];
  useEffect(() => {
    let mounted = true;
    let value;
    const asyncResult = callback(() => mounted);
    void Promise.resolve(asyncResult).then((result) => {
      value = result;
    });
    return () => {
      mounted = false;
      effectDestructor == null ? void 0 : effectDestructor(value);
    };
  }, effectDeps);
};

// packages/common/react-async/src/useControlledState.ts
import { useEffect as useEffect2, useState } from "react";
var useControlledState = (controlledValue, onChange, deps) => {
  const [value, setValue] = useState(controlledValue);
  useEffect2(() => {
    setValue(controlledValue);
  }, [
    controlledValue,
    ...deps != null ? deps : []
  ]);
  return [
    value,
    (callback) => {
      const newValue = typeof callback === "function" ? callback(value) : callback;
      setValue(newValue);
      onChange == null ? void 0 : onChange(newValue);
    }
  ];
};

// packages/common/react-async/src/useDynamicRef.ts
import { useEffect as useEffect3, useRef, useState as useState2 } from "react";
var useDynamicRef = (initialValue, deps) => {
  const [, setValue] = useState2(initialValue);
  const ref = useRef(initialValue());
  useEffect3(() => {
    ref.current = initialValue();
    setValue(ref.current);
  }, deps);
  return ref;
};

// packages/common/react-async/src/useMounted.ts
import { useEffect as useEffect4, useRef as useRef2 } from "react";
var useMounted = () => {
  const mounted = useRef2(false);
  useEffect4(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return () => mounted.current;
};

// packages/common/react-async/src/useStateRef.ts
import { useEffect as useEffect5, useRef as useRef3, useState as useState3 } from "react";
var useStateRef = (initialValue) => {
  const [value, setValue] = useState3(initialValue);
  const ref = useRef3();
  useEffect5(() => {
    ref.current = value;
  }, [
    initialValue,
    value
  ]);
  return [
    value,
    setValue,
    ref
  ];
};

// packages/common/react-async/src/useStateUpdater.ts
import update from "immutability-helper";
import { useState as useState4 } from "react";
var useStateUpdater = (initialValue) => {
  const [value, setValue] = useState4(initialValue);
  const handleUpdate = (spec) => {
    const newValue = update(value, spec);
    setValue(newValue);
    return newValue;
  };
  return [
    value,
    setValue,
    handleUpdate
  ];
};

// packages/common/react-async/src/useStateWithRef.ts
import { useEffect as useEffect6, useRef as useRef4, useState as useState5 } from "react";
var useStateWithRef = (initialValue) => {
  const [value, setValue] = useState5(initialValue);
  const ref = useRef4();
  useEffect6(() => {
    ref.current = value;
  }, [
    initialValue,
    value
  ]);
  return [
    value,
    setValue,
    ref
  ];
};

// packages/common/react-async/src/useTimestamp.ts
import { useEffect as useEffect7, useState as useState6 } from "react";
var useTimestamp = (deps) => {
  const [{ timestamp, previous }, setTimestamp] = useState6({
    timestamp: Date.now()
  });
  useEffect7(() => {
    setTimestamp({
      timestamp: Date.now(),
      previous: timestamp
    });
  }, deps || []);
  return [
    timestamp,
    () => setTimestamp({
      timestamp: Date.now(),
      previous: timestamp
    }),
    previous
  ];
};

// packages/common/react-async/src/useMulticastObservable.ts
import { useMemo, useSyncExternalStore } from "react";
var useMulticastObservable = (observable) => {
  const subscribeFn = useMemo(() => (listener) => {
    const subscription = observable.subscribe(listener);
    return () => subscription.unsubscribe();
  }, [
    observable
  ]);
  return useSyncExternalStore(subscribeFn, () => observable.get());
};
export {
  useAsyncEffect,
  useControlledState,
  useDynamicRef,
  useMounted,
  useMulticastObservable,
  useStateRef,
  useStateUpdater,
  useStateWithRef,
  useTimestamp
};
//# sourceMappingURL=index.mjs.map
